import React, { useState } from 'react';
import {
    Link,
    useRecordContext,
    useResourceContext,
    useTranslate,
    useNotify,
    useRefresh,
    useDataProvider,
    useLocaleState,
} from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import {
    IconButton,
    Stack,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Menu,
    MenuItem,
    TextField as MuiTextField,
    Alert,
    Box,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import DataObjectIcon from '@mui/icons-material/DataObject';
import BadgeIcon from '@mui/icons-material/Badge';
import { red } from '@mui/material/colors';

interface Props {
    showRemove?: boolean;
    showDebug?: boolean;
    label?: string;
    size?: 'small' | 'large';
}

interface MpassProfile {
    firstname: string | null;
    lastname: string | null;
    nickname: string | null;
    name_changed_from: string | null;
    last_login: string | null;
    schools: Array<{
        code: string;
        name: string;
        roles: string[];
        classes: string[];
        learningMaterialsCharges: string[];
    }>;
}

const DEBUG_ROLES = ['teacher', 'student', 'pupil'] as const;
const DEBUG_ROLE_LABELS: Record<(typeof DEBUG_ROLES)[number], string> = {
    teacher: 'Opettaja',
    student: 'Opiskelija',
    pupil: 'Oppilas',
};

type MpassLinkVariant = 'link' | 'new_link';
interface MpassDebugData {
    link?: string;
    new_link?: string;
    mpassdata?: object;
    masked?: boolean;
}
const DEBUG_VARIANT_LABELS: Record<MpassLinkVariant, string> = {
    link: 'alkuperäinen',
    new_link: 'uusi',
};

//Vaihtaa debug-linkin roolin, jotta samalla testidatalla voi testata ohjautumisen väärään sovellukseen
const swapDebugLinkRole = (link: string, role: string) =>
    link.replace(
        /\/login\/(teacher|student|pupil)\/debug\//,
        `/login/${role}/debug/`
    );

const DEBUG_STATE_MARKER = '/debug/mpass01_';

const formatNameChange = (value: string, locale: string) => {
    const match = value.match(/^(.*) \((\d{4}-\d{2}-\d{2}T[^)]+)\)$/);
    if (!match) return value;
    const date = new Date(match[2]);
    if (Number.isNaN(date.getTime())) return value;
    return `${match[1]} (${date.toLocaleString(locale, {
        dateStyle: 'short',
        timeStyle: 'short',
    })})`;
};

const decodeDebugData = (link: string): string => {
    const markerIndex = link.indexOf(DEBUG_STATE_MARKER);
    if (markerIndex === -1) {
        throw new Error('MPASS debug data was not found in the link');
    }

    const encoded = link
        .slice(markerIndex + DEBUG_STATE_MARKER.length)
        .split(/[?#]/, 1)[0];
    const base64 = encoded
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(encoded.length / 4) * 4, '=');
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};

const encodeDebugData = (data: string): string => {
    const bytes = new TextEncoder().encode(data);
    const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join(
        ''
    );
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

const SourceNameField = ({
    showRemove = false,
    showDebug = false,
    label = '',
    size,
}: Props) => {
    const translate = useTranslate();
    const [locale] = useLocaleState();
    const notify = useNotify();
    const [saving, setSaving] = useState(false);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [debugJsonText, setDebugJsonText] = useState('');
    const [debugLink, setDebugLink] = useState('');
    const [debugRole, setDebugRole] = useState<string | undefined>();
    const [debugReadOnly, setDebugReadOnly] = useState(false);
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [mpassProfile, setMpassProfile] = useState<MpassProfile | null>(null);
    const [debugTestData, setDebugTestData] = useState<MpassDebugData | null>(
        null
    );

    const record = useRecordContext();
    const resource = useResourceContext();
    const wrongDebugRoles = DEBUG_ROLES.filter(role => role !== resource);
    const { isLoading, canAccess } = useCanAccess({
        action: 'debug',
        resource: 'user',
        record,
    });
    const { canAccess: canReadMpassProfile } = useCanAccess({
        action: 'read_mpass_profile',
        resource: 'user',
        record,
    });
    const [debugMenuAnchor, setDebugMenuAnchor] = useState<null | HTMLElement>(
        null
    );

    const openMpassProfile = async () => {
        if (!canReadMpassProfile || !record) return;
        setSaving(true);
        try {
            const { data }: { data: MpassProfile } = await dataProvider.getAny(
                `user/${encodeURIComponent(record.guid)}/mpass-profile`,
                ''
            );
            setMpassProfile(data);
        } catch (error) {
            console.error(error);
            notify('notifications.ACTION_ERROR', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const loadDebugData = async (test: boolean): Promise<MpassDebugData> => {
        const response = await fetch(
            `${process.env.REACT_APP_INFRA_API}/teacher/${record?.guid}`,
            {
                method: 'PUT',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    school: localStorage.getItem('school'),
                    authorization: 'Bearer ' + localStorage.getItem('token'),
                },
                body: JSON.stringify({
                    ...record,
                    action: 'DEBUG_MPASS',
                    mpass_test: test,
                }),
            }
        );
        if (!response.ok)
            throw new Error(translate('notifications.ACTION_ERROR'));
        return response.json();
    };

    const openDebugMenu = async (anchor: HTMLElement) => {
        setSaving(true);
        setDebugTestData(null);
        try {
            const data = await loadDebugData(true);
            if (!data.new_link)
                throw new Error(translate('notifications.ACTION_ERROR'));
            setDebugTestData(data);
            setDebugMenuAnchor(anchor);
        } catch (error) {
            console.error(error);
            notify('notifications.ACTION_ERROR', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Loads the profile for editing before opening the selected debug flow.
    const openDebugEditor = async (
        variant: MpassLinkVariant,
        role?: string,
        readOnly = false
    ) => {
        setSaving(true);
        try {
            const data = readOnly ? await loadDebugData(false) : debugTestData;
            if (!data) throw new Error(translate('notifications.ACTION_ERROR'));
            const url = data[variant];
            if (
                readOnly &&
                (!data.mpassdata || typeof data.mpassdata !== 'object')
            ) {
                throw new Error(translate('notifications.ACTION_ERROR'));
            }
            if (!readOnly && (typeof url !== 'string' || !url)) {
                throw new Error(translate('notifications.ACTION_ERROR'));
            }

            const profileData =
                readOnly || variant === 'link'
                    ? JSON.stringify(data.mpassdata ?? {}, null, 2)
                    : JSON.stringify(JSON.parse(decodeDebugData(url)), null, 2);
            setDebugReadOnly(readOnly);
            setDebugLink(readOnly ? '' : url);
            setDebugRole(role);
            setDebugJsonText(profileData);
            setJsonDialogOpen(true);
            refresh();
        } catch (error) {
            notify(
                error instanceof Error
                    ? error.message
                    : translate('notifications.ACTION_ERROR'),
                { type: 'error' }
            );
        } finally {
            setSaving(false);
        }
    };

    const startMpassDebug = () => {
        const markerIndex = debugLink.indexOf(DEBUG_STATE_MARKER);
        if (markerIndex === -1) {
            notify(translate('notifications.ACTION_ERROR'), {
                type: 'error',
            });
            return;
        }

        const link =
            debugLink.slice(0, markerIndex + DEBUG_STATE_MARKER.length) +
            encodeDebugData(debugJsonText);
        window.open(
            debugRole ? swapDebugLinkRole(link, debugRole) : link,
            '_blank',
            'noopener,noreferrer'
        );
        setJsonDialogOpen(false);
    };

    const debugVariants: MpassLinkVariant[] = debugTestData?.masked
        ? ['new_link']
        : ['link', 'new_link'];
    const debugMenuItems = [
        ...debugVariants.map(variant => ({ role: undefined, variant })),
        ...wrongDebugRoles.flatMap(role =>
            debugVariants.map(variant => ({ role, variant }))
        ),
    ];

    if (isLoading || !record) return null;
    //console.log('canAccess', canAccess);

    // Get unique services by grouping them by display name
    const getDisplayName = (service: string) => {
        if (service === 'crm' || service === 'mpass') return service;
        return service
            .replace(/opepalvelu/g, 'oop')
            .replace(/fl_opettaja/g, 'fl')
            .replace(/fl_opiskelija/g, 'fl')
            .replace(/fl_opepalvelu/g, 'fl')
            .replace(/finnlectura/g, 'fl')
            .replace(/nova_.*/g, 'nova')
            .replace(
                /opiskelija/g,
                record?.username?.startsWith('e') &&
                    record?.username?.length < 10
                    ? 'kv'
                    : 'oop'
            )
            .replace(/adazure/g, 'ad')
            .toUpperCase();
    };

    // Create map of display name -> original service (keeping first occurrence)
    const uniqueServices = new Map<string, string>();
    record?.services?.forEach((service: string) => {
        const displayName = getDisplayName(service);
        if (!uniqueServices.has(displayName)) {
            uniqueServices.set(displayName, service);
        }
    });

    return (
        <>
            <Stack
                direction="row"
                alignItems={'baseline'}
                spacing={1}
                sx={{ fontSize: size === 'small' ? '0.7rem' : '1rem' }}
            >
                {Array.from(uniqueServices.entries()).map(
                    ([displayName, service], i) => {
                        switch (service) {
                            case 'crm':
                                return (
                                    <Tooltip
                                        title={translate(
                                            'resources.user.actions.open_crm'
                                        )}
                                        key={`${record.guid}_${i}${service}`}
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    bgcolor: 'white',
                                                    color: 'text.primary',
                                                    boxShadow: 1,
                                                    border: '1px solid #e0e0e0',
                                                },
                                            },
                                        }}
                                    >
                                        <span>
                                            <Link
                                                to={`${process.env.REACT_APP_CRM_URL}/main.aspx?appid=b68a0d15-8769-4bc0-8999-f949f7404bbb&pagetype=entityrecord&etn=contact&id=${record.crm_guid}`}
                                                target="_blank"
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                                style={{
                                                    textDecoration: 'none',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        color: '#fff',
                                                        padding: '1px 6px',
                                                        borderRadius: 3,
                                                        background:
                                                            'rgb(96, 125, 139, .5)',
                                                        transition:
                                                            'box-shadow 0.3s',
                                                    }}
                                                    onMouseEnter={e =>
                                                        (e.currentTarget.style.boxShadow =
                                                            '0 3px 6px rgba(0, 0, 0, 0.2)')
                                                    }
                                                    onMouseLeave={e =>
                                                        (e.currentTarget.style.boxShadow =
                                                            'none')
                                                    }
                                                >
                                                    {service.toUpperCase()}
                                                </span>
                                            </Link>
                                        </span>
                                    </Tooltip>
                                );
                            case 'mpass':
                                return (
                                    <div key={`${record.guid}_${i}${service}`}>
                                        <span
                                            style={{
                                                textDecoration: 'none',
                                                color: '#fff',
                                                padding: '1px 6px',
                                                borderRadius: 3,
                                                background:
                                                    'rgb(96, 125, 139, .5)',
                                            }}
                                        >
                                            {service.toUpperCase()}
                                        </span>
                                        {showRemove && (
                                            <Tooltip
                                                title={translate(
                                                    'resources.user.actions.remove_mpass'
                                                )}
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'white',
                                                            color: 'text.primary',
                                                            boxShadow: 1,
                                                            border: '1px solid #e0e0e0',
                                                        },
                                                    },
                                                }}
                                            >
                                                <IconButton
                                                    style={{
                                                        overflow: 'inherit',
                                                    }}
                                                    disabled={saving}
                                                    onClick={() => {
                                                        let action =
                                                            'REMOVE_MPASS';
                                                        let data = {
                                                            ...record,
                                                            ...{ action },
                                                        };
                                                        setSaving(true);
                                                        fetch(
                                                            `${process.env.REACT_APP_INFRA_API}/teacher/${record.guid}`,
                                                            {
                                                                method: 'PUT',
                                                                headers: {
                                                                    Accept: 'application/json',
                                                                    'Content-Type':
                                                                        'application/json',
                                                                    school: localStorage.getItem(
                                                                        'school'
                                                                    ),
                                                                    authorization:
                                                                        'Bearer ' +
                                                                        localStorage.getItem(
                                                                            'token'
                                                                        ),
                                                                },
                                                                body: JSON.stringify(
                                                                    data
                                                                ),
                                                            }
                                                        ).then(ret => {
                                                            setSaving(false);
                                                            if (
                                                                ret.status !==
                                                                200
                                                            ) {
                                                                notify(
                                                                    translate(
                                                                        'notifications.ACTION_ERROR'
                                                                    ),
                                                                    {
                                                                        type: 'error',
                                                                    }
                                                                );
                                                            } else {
                                                                /*All good*/
                                                                setSaving(
                                                                    false
                                                                );
                                                                notify(
                                                                    translate(
                                                                        'notifications.MPASS_REMOVED'
                                                                    ),
                                                                    {
                                                                        type: 'success',
                                                                    }
                                                                );
                                                                refresh();
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <DeleteIcon
                                                        style={{
                                                            color: red[500],
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {showDebug && canReadMpassProfile && (
                                            <Tooltip
                                                title={translate(
                                                    'resources.user.actions.mpass_profile'
                                                )}
                                            >
                                                <IconButton
                                                    disabled={saving}
                                                    onClick={openMpassProfile}
                                                >
                                                    <BadgeIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {showDebug &&
                                            canAccess &&
                                            process.env
                                                .REACT_APP_MPASS_DEBUG_LINKS_ENABLED ===
                                                'true' && (
                                                <>
                                                    <Tooltip
                                                        title={translate(
                                                            'resources.user.actions.debug_mpass_menu'
                                                        )}
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    bgcolor:
                                                                        'white',
                                                                    color: 'text.primary',
                                                                    boxShadow: 1,
                                                                    border: '1px solid #e0e0e0',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <IconButton
                                                            style={{
                                                                overflow:
                                                                    'inherit',
                                                                marginLeft:
                                                                    '-8px',
                                                            }}
                                                            disabled={saving}
                                                            onClick={e =>
                                                                openDebugMenu(
                                                                    e.currentTarget
                                                                )
                                                            }
                                                        >
                                                            <PersonIcon
                                                                style={{
                                                                    color: '#1976d2',
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Menu
                                                        anchorEl={
                                                            debugMenuAnchor
                                                        }
                                                        open={Boolean(
                                                            debugMenuAnchor
                                                        )}
                                                        onClose={() =>
                                                            setDebugMenuAnchor(
                                                                null
                                                            )
                                                        }
                                                    >
                                                        {debugMenuItems.map(
                                                            ({
                                                                role,
                                                                variant,
                                                            }) => (
                                                                <MenuItem
                                                                    key={`${role ?? 'own'}_${variant}`}
                                                                    onClick={() => {
                                                                        setDebugMenuAnchor(
                                                                            null
                                                                        );
                                                                        openDebugEditor(
                                                                            variant,
                                                                            role
                                                                        );
                                                                    }}
                                                                >
                                                                    {debugTestData?.masked
                                                                        ? `${translate('resources.user.actions.debug_mpass_masked')}${role ? ` \u2013 ${DEBUG_ROLE_LABELS[role as keyof typeof DEBUG_ROLE_LABELS]}` : ''}`
                                                                        : role
                                                                          ? `${translate('resources.user.actions.debug_mpass_wrong_app')} \u2013 ${DEBUG_ROLE_LABELS[role as keyof typeof DEBUG_ROLE_LABELS]} (${DEBUG_VARIANT_LABELS[variant]})`
                                                                          : translate(
                                                                                variant ===
                                                                                    'link'
                                                                                    ? 'resources.user.actions.debug_mpass_original'
                                                                                    : 'resources.user.actions.debug_mpass_new'
                                                                            )}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Menu>
                                                </>
                                            )}

                                        {showDebug &&
                                            canAccess &&
                                            process.env
                                                .REACT_APP_MPASS_DEBUG_DATA_ENABLED ===
                                                'true' && (
                                                <Tooltip
                                                    title={translate(
                                                        'resources.user.actions.debug_mpass_json'
                                                    )}
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor:
                                                                    'white',
                                                                color: 'text.primary',
                                                                boxShadow: 1,
                                                                border: '1px solid #e0e0e0',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <IconButton
                                                        style={{
                                                            overflow: 'inherit',
                                                            marginLeft: '-16px',
                                                        }}
                                                        disabled={saving}
                                                        onClick={() => {
                                                            openDebugEditor(
                                                                'link',
                                                                undefined,
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        <DataObjectIcon
                                                            style={{
                                                                color: '#ff9800',
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                    </div>
                                );
                            case 'adazure':
                            case 'opepalvelu':
                            case 'finnlectura':
                            case 'opiskelija':
                            case 'edustore':
                            case 'fl_opepalvelu':
                            case 'fl_opiskelija':
                            case 'fl_opettaja':
                            case 'nova_opiskelija':
                            case 'nova_opettaja':
                            case 'nova_oppilas':
                                return (
                                    <div key={`${record.guid}_${i}${service}`}>
                                        <span
                                            style={{
                                                textDecoration: 'none',
                                                color: '#fff',
                                                padding: '1px 6px',
                                                borderRadius: 3,
                                                background:
                                                    'rgb(96, 125, 139, .5)',
                                            }}
                                        >
                                            {displayName}
                                        </span>
                                    </div>
                                );
                            default:
                                return null;
                        }
                    }
                )}
            </Stack>

            <Dialog
                open={Boolean(mpassProfile)}
                onClose={() => setMpassProfile(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {translate('resources.user.actions.mpass_profile')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2">
                        {translate('resources.user.actions.mpass_name')}
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        {[mpassProfile?.firstname, mpassProfile?.lastname]
                            .filter(Boolean)
                            .join(' ') || '—'}
                    </Typography>
                    <Typography variant="subtitle2">
                        {translate('resources.user.actions.mpass_nickname')}
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        {mpassProfile?.nickname || '—'}
                    </Typography>
                    {mpassProfile?.name_changed_from && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {translate(
                                'resources.user.actions.mpass_name_changed',
                                {
                                    previous: formatNameChange(
                                        mpassProfile.name_changed_from,
                                        locale
                                    ),
                                }
                            )}
                            <Typography variant="subtitle2">
                                {translate(
                                    'resources.user.actions.mpass_last_login'
                                )}
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                {mpassProfile?.last_login
                                    ? Number.isNaN(
                                          new Date(
                                              mpassProfile.last_login
                                          ).getTime()
                                      )
                                        ? mpassProfile.last_login
                                        : new Date(
                                              mpassProfile.last_login
                                          ).toLocaleString(locale, {
                                              dateStyle: 'short',
                                              timeStyle: 'short',
                                          })
                                    : translate(
                                          'resources.student.fields.materialcharge.unknown'
                                      )}
                            </Typography>
                        </Alert>
                    )}
                    <Typography variant="subtitle2">
                        {translate('resources.user.actions.mpass_schools')}
                    </Typography>
                    {mpassProfile?.schools.length ? (
                        <Box component="ul" sx={{ pl: 3 }}>
                            {mpassProfile.schools.map((school, index) => (
                                <li key={`${school.code}-${index}`}>
                                    {school.name || '—'}
                                    {school.code ? ` (${school.code})` : ''}
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {translate(
                                            'resources.user.actions.mpass_roles'
                                        )}
                                        : {school.roles.join(', ') || '—'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {translate(
                                            'resources.user.actions.mpass_classes'
                                        )}
                                        : {school.classes.join(', ') || '—'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {translate(
                                            'resources.student.fields.materialcharge.title'
                                        )}
                                        :{' '}
                                        {school.learningMaterialsCharges.length
                                            ? school.learningMaterialsCharges
                                                  .map(value =>
                                                      value === '1'
                                                          ? translate(
                                                                'resources.student.fields.materialcharge.yes'
                                                            )
                                                          : value === '0'
                                                            ? translate(
                                                                  'resources.student.fields.materialcharge.no'
                                                              )
                                                            : value
                                                  )
                                                  .join(', ')
                                            : translate(
                                                  'resources.student.fields.materialcharge.unknown'
                                              )}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                    ) : (
                        <Typography>—</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMpassProfile(null)}>
                        {translate('ra.action.close')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {translate(
                        debugReadOnly
                            ? 'resources.user.actions.debug_mpass_json'
                            : 'resources.user.actions.debug_mpass_dialog_title'
                    )}
                </DialogTitle>
                <DialogContent>
                    {!debugReadOnly &&
                        debugLink === debugTestData?.new_link && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {translate(
                                    'resources.user.actions.debug_mpass_masked_help'
                                )}
                            </Alert>
                        )}
                    {!debugReadOnly && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {translate(
                                'resources.user.actions.debug_mpass_json_help'
                            )}
                        </Alert>
                    )}
                    <MuiTextField
                        label={translate(
                            'resources.user.actions.debug_mpass_json'
                        )}
                        value={debugJsonText}
                        onChange={event => setDebugJsonText(event.target.value)}
                        multiline
                        minRows={16}
                        fullWidth
                        InputProps={{ readOnly: debugReadOnly }}
                        inputProps={{
                            style: {
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setJsonDialogOpen(false)}
                        color="primary"
                    >
                        {translate('resources.user.actions.debug_mpass_cancel')}
                    </Button>
                    {!debugReadOnly && (
                        <Button
                            onClick={startMpassDebug}
                            variant="contained"
                            color="primary"
                        >
                            {translate(
                                'resources.user.actions.debug_mpass_start'
                            )}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SourceNameField;
